import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { TCLIconImage } from '../../components/TCLMark';
import { useFacilitateAuth } from '../../context/FacilitateAuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FacilitateLogin = () => {
  const navigate = useNavigate();
  const { login } = useFacilitateAuth();

  // Login state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [tempToken, setTempToken] = useState(null);
  const [step, setStep] = useState('credentials'); // 'credentials' | 'otp'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Request access state
  const [mode, setMode] = useState('login'); // 'login' | 'request'
  const [reqForm, setReqForm] = useState({ name: '', email: '', organisation: '', message: '' });
  const [reqSubmitted, setReqSubmitted] = useState(false);
  const [reqError, setReqError] = useState('');
  const [reqLoading, setReqLoading] = useState(false);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/sessions/speakers/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();
      if (res.ok && data.requires_otp) {
        setTempToken(data.temp_token);
        setStep('otp');
      } else if (!res.ok) {
        setError(data.detail || 'Login failed');
      }
    } catch {
      setError('Network error');
    }
    setLoading(false);
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/sessions/speakers/verify-otp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ temp_token: tempToken, otp_code: otpCode }),
      });
      const data = await res.json();
      if (res.ok) {
        login(data.access_token, data.speaker);
        navigate('/facilitate');
      } else {
        setError(data.detail || 'Invalid code');
      }
    } catch {
      setError('Network error');
    }
    setLoading(false);
  };

  const handleRequestAccess = async (e) => {
    e.preventDefault();
    setReqLoading(true);
    setReqError('');
    try {
      const res = await fetch(`${API_URL}/api/sessions/public/request-facilitator-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: reqForm.name.trim(),
          email: reqForm.email.trim(),
          organisation: reqForm.organisation.trim() || null,
          message: reqForm.message.trim() || null,
        }),
      });
      if (res.ok) {
        setReqSubmitted(true);
      } else {
        const err = await res.json().catch(() => ({}));
        setReqError(err.detail || 'Submission failed. Please try again.');
      }
    } catch {
      setReqError('Network error. Please try again.');
    }
    setReqLoading(false);
  };

  return (
    <div className="min-h-screen bg-[#071B3A] flex items-center justify-center px-6 py-12">
      <Helmet><meta name="robots" content="noindex, nofollow, noarchive" /></Helmet>
      <div className="max-w-sm w-full">

        {/* Header */}
        <div className="text-center mb-8">
          <TCLIconImage size={52} className="mx-auto mb-3" />
          <h1 className="font-serif text-2xl text-[#FAF8F3] font-light">Facilitator Portal</h1>
          <p className="text-[#FAF8F3]/30 text-sm mt-2">Third Culture Leadership · CFQ Sessions</p>
        </div>

        {/* Tab toggle */}
        <div className="flex bg-white/5 rounded-xl p-1 mb-6">
          <button
            onClick={() => { setMode('login'); setError(''); }}
            className={"flex-1 py-2 rounded-lg text-sm font-medium transition-all " + (mode === 'login' ? 'bg-[#C99316] text-[#071B3A]' : 'text-[#FAF8F3]/40 hover:text-[#FAF8F3]/70')}
            data-testid="tab-login"
          >
            Sign In
          </button>
          <button
            onClick={() => { setMode('request'); setReqError(''); }}
            className={"flex-1 py-2 rounded-lg text-sm font-medium transition-all " + (mode === 'request' ? 'bg-[#C99316] text-[#071B3A]' : 'text-[#FAF8F3]/40 hover:text-[#FAF8F3]/70')}
            data-testid="tab-request-access"
          >
            Request Access
          </button>
        </div>

        {/* ── Login panel ── */}
        {mode === 'login' && (
          <>
            {step === 'credentials' ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <input
                  type="email" value={email} onChange={e => setEmail(e.target.value)}
                  placeholder="Email" required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#FAF8F3] text-sm placeholder-white/20 focus:outline-none focus:border-[#C99316]/30"
                  data-testid="facilitate-email-input"
                />
                <input
                  type="password" value={password} onChange={e => setPassword(e.target.value)}
                  placeholder="Password" required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#FAF8F3] text-sm placeholder-white/20 focus:outline-none focus:border-[#C99316]/30"
                  data-testid="facilitate-password-input"
                />
                {error && <p className="text-red-400 text-sm" data-testid="facilitate-login-error">{error}</p>}
                <button type="submit" disabled={loading}
                  className="w-full bg-[#C99316] text-[#071B3A] py-3 rounded-lg text-sm font-semibold hover:bg-[#C99316]/90 disabled:opacity-50 transition-colors"
                  data-testid="facilitate-login-btn">
                  {loading ? 'Signing in…' : 'Sign In'}
                </button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div className="bg-white/5 border border-white/10 rounded-lg p-4 text-center mb-2">
                  <p className="text-[#FAF8F3]/70 text-sm">A verification code has been sent to</p>
                  <p className="text-[#C99316] text-sm font-medium mt-1">{email}</p>
                </div>
                <input
                  type="text" value={otpCode}
                  onChange={e => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000" required maxLength={6}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#FAF8F3] text-2xl font-mono text-center tracking-[0.5em] placeholder-white/20 focus:outline-none focus:border-[#C99316]/30"
                  data-testid="facilitate-otp-input"
                  autoFocus
                />
                <p className="text-[#FAF8F3]/30 text-xs text-center">Code expires in 10 minutes</p>
                {error && <p className="text-red-400 text-sm text-center" data-testid="facilitate-otp-error">{error}</p>}
                <button type="submit" disabled={loading || otpCode.length < 6}
                  className="w-full bg-[#C99316] text-[#071B3A] py-3 rounded-lg text-sm font-semibold hover:bg-[#C99316]/90 disabled:opacity-50"
                  data-testid="facilitate-otp-submit">
                  {loading ? 'Verifying…' : 'Verify & Enter'}
                </button>
                <button type="button" onClick={() => { setStep('credentials'); setOtpCode(''); setError(''); }}
                  className="w-full py-2 text-[#FAF8F3]/40 hover:text-[#FAF8F3]/70 text-sm transition-colors">
                  Back to login
                </button>
              </form>
            )}

            <p className="text-center text-[#FAF8F3]/20 text-xs mt-6">
              Don't have an account?{' '}
              <button onClick={() => setMode('request')} className="text-[#C99316]/60 hover:text-[#C99316] underline underline-offset-2">
                Request access
              </button>
            </p>
          </>
        )}

        {/* ── Request Access panel ── */}
        {mode === 'request' && (
          <>
            {reqSubmitted ? (
              <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-xl p-6 text-center" data-testid="request-access-success">
                <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <p className="text-[#FAF8F3] text-sm font-medium mb-1">Request submitted</p>
                <p className="text-[#FAF8F3]/40 text-xs leading-relaxed">We'll review your request and send you a setup link within 24 hours.</p>
                <button onClick={() => { setMode('login'); setReqSubmitted(false); setReqForm({ name: '', email: '', organisation: '', message: '' }); }}
                  className="mt-4 text-[#C99316]/60 hover:text-[#C99316] text-xs underline underline-offset-2">
                  Back to login
                </button>
              </div>
            ) : (
              <form onSubmit={handleRequestAccess} className="space-y-4" data-testid="request-access-form">
                <div className="bg-[#C99316]/8 border border-[#C99316]/15 rounded-xl p-4 mb-2">
                  <p className="text-[#FAF8F3]/50 text-xs leading-relaxed">
                    Request access to use the CFQ with live audiences at your events. Once approved, you'll receive a setup email within 24 hours.
                  </p>
                </div>
                <input
                  type="text" value={reqForm.name}
                  onChange={e => setReqForm({ ...reqForm, name: e.target.value })}
                  placeholder="Your full name" required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#FAF8F3] text-sm placeholder-white/20 focus:outline-none focus:border-[#C99316]/30"
                  data-testid="req-name-input"
                />
                <input
                  type="email" value={reqForm.email}
                  onChange={e => setReqForm({ ...reqForm, email: e.target.value })}
                  placeholder="Work email" required
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#FAF8F3] text-sm placeholder-white/20 focus:outline-none focus:border-[#C99316]/30"
                  data-testid="req-email-input"
                />
                <input
                  type="text" value={reqForm.organisation}
                  onChange={e => setReqForm({ ...reqForm, organisation: e.target.value })}
                  placeholder="Organisation (optional)"
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#FAF8F3] text-sm placeholder-white/20 focus:outline-none focus:border-[#C99316]/30"
                  data-testid="req-org-input"
                />
                <textarea
                  value={reqForm.message}
                  onChange={e => setReqForm({ ...reqForm, message: e.target.value })}
                  placeholder="Briefly describe your event or how you'd use the CFQ (optional)"
                  rows={3}
                  className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#FAF8F3] text-sm placeholder-white/20 focus:outline-none focus:border-[#C99316]/30 resize-none"
                  data-testid="req-message-input"
                />
                {reqError && <p className="text-red-400 text-sm" data-testid="req-error">{reqError}</p>}
                <button type="submit" disabled={reqLoading}
                  className="w-full bg-[#C99316] text-[#071B3A] py-3 rounded-lg text-sm font-semibold hover:bg-[#C99316]/90 disabled:opacity-50 transition-colors"
                  data-testid="req-submit-btn">
                  {reqLoading ? 'Submitting…' : 'Request Access'}
                </button>
              </form>
            )}
          </>
        )}

      </div>
    </div>
  );
};

export default FacilitateLogin;
